import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, themes, ThemeName } from '../contexts/ThemeContext';
import { Layout } from '../components/Layout';
import { User, Palette, Check, CreditCard as Edit2, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';

const Profile: React.FC = () => {
  const { user, reload } = useAuth();
  const { themeName, setTheme, isLoading } = useTheme();
  const [isChangingTheme, setIsChangingTheme] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    nome: user?.nome || '',
    email: user?.email || '',
    cargo: user?.cargo || '',
    departamento: user?.departamento || '',
    telefone: user?.telefone || '',
    ramal: user?.ramal || '',
  });

  const handleThemeChange = async (newTheme: ThemeName) => {
    setIsChangingTheme(true);
    try {
      await setTheme(newTheme);
      toast.success('Tema alterado com sucesso!');
    } catch (error) {
      toast.error('Erro ao alterar tema');
      console.error('Error changing theme:', error);
    } finally {
      setIsChangingTheme(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setFormData({
        nome: user?.nome || '',
        email: user?.email || '',
        cargo: user?.cargo || '',
        departamento: user?.departamento || '',
        telefone: user?.telefone || '',
        ramal: user?.ramal || '',
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!user?.dbId) {
      toast.error('ID do usuario nao encontrado.');
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('usuarios')
        .update({
          nome: formData.nome,
          cargo: formData.cargo,
          departamento: formData.departamento,
          telefone: formData.telefone,
          ramal: formData.ramal,
        })
        .eq('id', user.dbId);

      if (error) throw error;

      toast.success('Dados atualizados com sucesso!');
      setIsEditing(false);
      await reload();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Erro ao salvar dados');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-headline-lg text-on-surface mb-1">Meu Perfil</h1>
          <p className="text-sm text-secondary">Gerencie suas informações pessoais e preferências</p>
        </div>
        {!isEditing ? (
          <button
            onClick={handleEditToggle}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-primary hover:bg-primary-dark transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            Editar Perfil
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              onClick={handleEditToggle}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 border border-outline-variant text-on-surface rounded-lg hover:bg-surface-container-low transition-colors disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              Cancelar
            </button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-outline-variant dark:border-gray-700 p-6 mb-4">
        <div className="flex items-start gap-4 mb-6">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 bg-primary"
          >
            {user?.nome?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-on-surface mb-1">
              {user?.nome || 'Usuário'}
            </h2>
            <p className="text-sm text-secondary mb-2">{user?.email}</p>
            <div className="flex gap-4 text-sm">
              <span className="text-secondary">
                <strong>Cargo:</strong> {user?.cargo || 'Não informado'}
              </span>
              <span className="text-secondary">
                <strong>Departamento:</strong> {user?.departamento || 'Não informado'}
              </span>
            </div>
          </div>
        </div>

        <div className="border-t border-outline-variant pt-6">
          <h3 className="text-lg font-bold text-on-surface mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Informações Pessoais
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                Nome Completo
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              ) : (
                <p className="px-3 py-2 bg-surface-container-low rounded-lg text-on-surface">
                  {user?.nome || 'Não informado'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                E-mail
              </label>
              <p className="px-3 py-2 bg-surface-container rounded-lg text-secondary">
                {user?.email}
              </p>
              <p className="text-xs text-secondary mt-1">O e-mail não pode ser alterado</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                Cargo
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.cargo}
                  onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                  className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              ) : (
                <p className="px-3 py-2 bg-surface-container-low rounded-lg text-on-surface">
                  {user?.cargo || 'Não informado'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                Departamento
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.departamento}
                  onChange={(e) => setFormData({ ...formData, departamento: e.target.value })}
                  className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              ) : (
                <p className="px-3 py-2 bg-surface-container-low rounded-lg text-on-surface">
                  {user?.departamento || 'Não informado'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                Telefone
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              ) : (
                <p className="px-3 py-2 bg-surface-container-low rounded-lg text-on-surface">
                  {user?.telefone || 'Não informado'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface mb-1">
                Ramal
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.ramal}
                  onChange={(e) => setFormData({ ...formData, ramal: e.target.value })}
                  placeholder="0000"
                  className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              ) : (
                <p className="px-3 py-2 bg-surface-container-low rounded-lg text-on-surface">
                  {user?.ramal || 'Não informado'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-outline-variant dark:border-gray-700 p-4">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-on-surface mb-1 flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Personalização de Tema
          </h2>
          <p className="text-sm text-secondary">Escolha sua paleta de cores e veja as mudanças aplicadas no menu</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {Object.values(themes).map((themeOption) => {
            const isSelected = themeName === themeOption.name;

            return (
              <button
                key={themeOption.name}
                onClick={() => handleThemeChange(themeOption.name)}
                disabled={isChangingTheme}
                className={`
                  relative p-3 rounded-lg border-2 transition-all duration-300 hover:scale-105
                  ${isSelected
                    ? 'border-current shadow-lg'
                    : 'border-outline-variant hover:border-primary/30 shadow-sm hover:shadow-md'
                  }
                  ${isChangingTheme ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                style={{
                  borderColor: isSelected ? themeOption.colors.primary : undefined,
                }}
              >
                {isSelected && (
                  <div
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white shadow-lg"
                    style={{ backgroundColor: themeOption.colors.primary }}
                  >
                    <Check className="w-3 h-3" />
                  </div>
                )}

                <div className="mb-2">
                  <div
                    className="w-full h-12 rounded-md shadow-inner mb-2"
                    style={{ background: themeOption.colors.gradient }}
                  />
                  <div className="flex gap-1">
                    <div
                      className="flex-1 h-3 rounded"
                      style={{ backgroundColor: themeOption.colors.primary }}
                    />
                    <div
                      className="flex-1 h-3 rounded"
                      style={{ backgroundColor: themeOption.colors.primaryLight }}
                    />
                    <div
                      className="flex-1 h-3 rounded"
                      style={{ backgroundColor: themeOption.colors.accent }}
                    />
                  </div>
                </div>

                <h3 className="font-semibold text-xs text-on-surface text-center">
                  {themeOption.displayName}
                </h3>
              </button>
            );
          })}
        </div>

        <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <p className="text-xs text-primary">
            <strong>Dica:</strong> Clique em um tema para aplicá-lo instantaneamente. Observe as mudanças no menu lateral e nos botões!
          </p>
        </div>
      </div>
    </div>
    </Layout>
  );
};

export default Profile;
